// Persistent left-rail — always visible across every wizard step.
// Renders the target UGC's thumbnail + source pill + any existing
// operator/detect attachments as small chips so the operator can see
// what they're building on without leaving the current step.
//
// Skeleton state (media not yet loaded) reserves the same footprint
// so the wizard doesn't jump when data arrives.

import { Box, VStack, HStack, Text, Badge, Image, Skeleton, SkeletonText, Grid, GridItem } from '@chakra-ui/react';
import type { MediaAssignments } from './types';

type Props = {
  // Primary UGC's assignments — drives the big thumbnail + summary. In
  // single-UGC mode this is the only render; in batch mode it's the
  // "hero" pick with a strip of siblings below.
  assignments:        MediaAssignments | null;
  // Phase 7 batch — full map so we can render a thumbnail strip of
  // secondary picks alongside the primary hero.
  assignmentsByMedia: Map<string, MediaAssignments>;
  mediaIds:           string[];
  loading:            boolean;
  error:              string | null;
};

export function UgcPreviewRail({ assignments, assignmentsByMedia, mediaIds, loading, error }: Props) {
  const batchCount = mediaIds.length;
  return (
    <Box
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="lg"
      bg="brand.surface"
      p={4}
      position="sticky"
      top={4}
      alignSelf="flex-start"
      w="260px"
      flexShrink={0}
    >
      <HStack justify="space-between" mb={3}>
        <Text
          fontSize="11px"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.06em"
          color="brand.muted"
        >
          UGC
        </Text>
        {batchCount > 1 && (
          <Badge fontSize="9px" colorScheme="purple" variant="solid">
            Batch × {batchCount}
          </Badge>
        )}
      </HStack>

      {loading && (
        <VStack align="stretch" spacing={3}>
          <Skeleton height="220px" borderRadius="md" />
          <SkeletonText noOfLines={2} spacing={2} />
        </VStack>
      )}

      {error && !loading && (
        <Text fontSize="sm" color="red.600">{error}</Text>
      )}

      {assignments && !loading && (
        <VStack align="stretch" spacing={3}>
          <Box
            borderRadius="md"
            overflow="hidden"
            bg="brand.canvas"
            borderWidth="1px"
            borderColor="brand.border"
          >
            {assignments.media.fileType === 'video' && assignments.media.fileUrl ? (
              // This is a persistent, always-visible side-by-side rail —
              // "watch the source video while you fill out the form" —
              // not a gallery tile among many. HoverPlayVideo was built
              // for the latter (play-on-hover to cap simultaneous video
              // streams in a grid) and is the wrong fit here: gating
              // playback on the pointer staying over the rail defeats the
              // entire point of a side-by-side layout the moment the
              // operator moves to the form, it shows a black box + ▶
              // instead of a real frame at rest (no poster is available
              // for raw UGC media), and touch users would have no way to
              // play it at all. One always-on video on a wizard screen
              // was never the perf problem PR #61 set out to fix — N
              // autoplaying tiles in a grid was. See PR #61 review.
              <video
                src={assignments.media.fileUrl}
                muted
                loop
                autoPlay
                playsInline
                style={{ display: 'block', width: '100%', maxHeight: 320, objectFit: 'cover' }}
              />
            ) : assignments.media.fileUrl ? (
              <Image
                src={assignments.media.fileUrl}
                alt="UGC preview"
                w="100%"
                maxH="320px"
                objectFit="cover"
                display="block"
              />
            ) : (
              <Box p={4}>
                <Text fontSize="xs" color="brand.muted">No preview URL</Text>
              </Box>
            )}
          </Box>

          <HStack spacing={2} flexWrap="wrap">
            {assignments.media.source && (
              <Badge fontSize="9px" colorScheme="purple" variant="subtle">
                {assignments.media.source}
              </Badge>
            )}
            {assignments.media.socialPostType && (
              <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                {assignments.media.socialPostType}
              </Badge>
            )}
            {assignments.media.externalId && (
              <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                {assignments.media.externalId.slice(0, 10)}
              </Badge>
            )}
          </HStack>

          {batchCount > 1 && (
            <Box>
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={1}>
                Also in batch
              </Text>
              <Grid templateColumns="repeat(4, 1fr)" gap={1}>
                {mediaIds.slice(1, 9).map(id => {
                  const a = assignmentsByMedia.get(id);
                  const url = a?.media.fileUrl;
                  return (
                    <GridItem key={id} bg="brand.canvas" borderRadius="sm" overflow="hidden">
                      <Box aspectRatio={1}>
                        {a?.media.fileType === 'video' && url ? (
                          <video src={url} muted loop playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        ) : url ? (
                          <Image src={url} alt="" w="100%" h="100%" objectFit="cover" />
                        ) : null}
                      </Box>
                    </GridItem>
                  );
                })}
              </Grid>
              {batchCount > 9 && (
                <Text fontSize="10px" color="brand.muted" mt={1}>
                  +{batchCount - 9} more
                </Text>
              )}
            </Box>
          )}

          <AttachmentSummary a={assignments} />
        </VStack>
      )}
    </Box>
  );
}

// Compact rollup of what's already attached to this UGC. Operator
// entries render solid; detect-derived entries render muted so the
// distinction the Phase 1 schema encodes (source: 'operator' vs
// 'detect') is visible from the rail. Kept intentionally short —
// the full pre-checked state lives inside Step 2 (2c).
function AttachmentSummary({ a }: { a: MediaAssignments }) {
  const { counts } = a;
  const anyOperator = counts.operatorProducts + counts.operatorCategories + counts.branding + counts.promotional > 0;
  const anyDetect   = counts.detectProducts + counts.detectCategories > 0;

  if (!anyOperator && !anyDetect) {
    return (
      <Text fontSize="11px" color="brand.muted">
        No attachments yet.
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={1}>
      <Text
        fontSize="10px"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="0.06em"
        color="brand.muted"
      >
        Attached
      </Text>
      {counts.operatorProducts > 0 && (
        <Text fontSize="11px" color="brand.ink">
          {counts.operatorProducts} product{counts.operatorProducts === 1 ? '' : 's'} (operator)
        </Text>
      )}
      {counts.detectProducts > 0 && (
        <Text fontSize="11px" color="brand.muted">
          {counts.detectProducts} product{counts.detectProducts === 1 ? '' : 's'} (detect)
        </Text>
      )}
      {counts.operatorCategories > 0 && (
        <Text fontSize="11px" color="brand.ink">
          {counts.operatorCategories} categor{counts.operatorCategories === 1 ? 'y' : 'ies'} (operator)
        </Text>
      )}
      {counts.detectCategories > 0 && (
        <Text fontSize="11px" color="brand.muted">
          {counts.detectCategories} categor{counts.detectCategories === 1 ? 'y' : 'ies'} (detect)
        </Text>
      )}
      {counts.branding > 0 && (
        <Text fontSize="11px" color="brand.ink">Branding attached</Text>
      )}
      {counts.promotional > 0 && (
        <Text fontSize="11px" color="brand.ink">Promotional attached</Text>
      )}
    </VStack>
  );
}
